<template>
  <v-dialog
    v-model="isVisible"
    max-width="600"
    persistent
    @keydown.esc="closeModal"
  >
    <v-card class="announcement-card" elevation="8">
      <button class="close-btn" @click="closeModal">
        <v-icon class="close-icon">mdi-close</v-icon>
      </button>

      <!-- Image Banner -->
      <div class="announcement-banner">
        <img
          :src="getBannerImage()"
          :alt="announcement.title"
          class="banner-image"
          @error="onBannerImageError"
        />
      </div>

      <!-- Content Section -->
      <div class="announcement-content">
        <h2 class="announcement-title">{{ announcement.title }}</h2>
        
        <p v-if="announcement.subtitle" class="announcement-subtitle">
          {{ announcement.subtitle }}
        </p>

        <div v-if="announcement.description" class="announcement-description" v-html="announcement.description"></div>

        <div class="announcement-actions">
          <a
            v-if="announcement.linkText && announcement.link"
            :href="announcement.link"
            target="_blank"
            rel="noopener noreferrer"
            class="cta-link-button"
          >
            {{ announcement.linkText }}
            <v-icon class="arrow-icon">mdi-arrow-right</v-icon>
          </a>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
const defaultBannerImage = '/assets/images/SDD.png';

export default {
  name: 'AnnouncementModal',
  props: {
    announcement: {
      type: Object,
      required: false,
      default: () => ({
        title: '',
        subtitle: '',
        description: '',
        imageUrl: null,
        link: '',
        linkText: '',
      })
      // validator: (value) => {
      //   return typeof value === 'object' && value !== null;
      // }
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isVisible: this.visible,
    };
  },
  watch: {
    visible(newVal) {
      this.isVisible = newVal;
    },
    isVisible(newVal) {
      this.$emit('update:visible', newVal);
    },
  },
  methods: {
    getBannerImage() {
      // announcements without an image show the default logo
      let retVal = this.announcement.imageUrl || defaultBannerImage;
      return retVal;
    },
    onBannerImageError(event) {
      // a broken image url falls back to the default logo (once, to avoid an error loop)
      if (!event.target.src.endsWith(defaultBannerImage)) {
        event.target.src = defaultBannerImage;
      }
    },
    closeModal() {
      this.isVisible = false;
      this.$emit('close');
    },
  },
  mounted() {
    
    this.announcement;
    // Focus the close button when the modal opens for better accessibility
    this.$nextTick(() => {
      const closeBtn = this.$el.querySelector('.close-btn');
      if (closeBtn) {
        closeBtn.focus();
      }
    });
  },
};
</script>

<style scoped lang="scss">
.announcement-card {
  position: relative;
  border-radius: 16px;
  overflow: hidden;
}

.announcement-banner {
  position: relative;
  background: linear-gradient(135deg, #f5f7fa 0%, #edf0f2 100%);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  /* side padding keeps the close button off the image */
  padding: 24px 44px;
}

.banner-image {
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 280px;
  object-fit: contain;
  border-radius: 8px;
}

.close-btn {
  position: absolute;
  top: 10px;
  inset-inline-end: 10px;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  color: #777fa1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;

  .close-icon {
    font-size: 20px;
    color: inherit;
  }

  &:hover {
    color: #024976;
  }
}

.announcement-content {
  padding: 28px;
}

.announcement-title {
  font-size: 20px;
  font-weight: 500;
  color: #000;
  margin: 0 0 12px 0;
  padding-inline-end: 28px;
  line-height: 1.3;
}

.announcement-subtitle {
  font-size: 14px;
  color: #374151;
  margin: 0 0 12px 0;
  line-height: 1.55;
}

.announcement-description {
  font-size: 13.5px;
  color: #777fa1;
  line-height: 1.65;
  margin: 0 0 22px 0;

  ::v-deep p {
    margin: 0 0 6px 0;
  }

  ::v-deep ul,
  ::v-deep ol {
    margin: 0 0 6px 0;
    padding-inline-start: 22px;
  }
}

.announcement-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.cta-link-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #2a7f8f;
  color: white;
  text-decoration: none;
  font-weight: 500;
  padding: 12px 28px;
  border-radius: 50px;
  transition: all 0.3s ease;
  font-size: 15px;

  &:hover {
    background: #1f5a6f;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .arrow-icon {
    font-size: 18px;
    margin-left: 4px;
  }
}

/* Responsive Design */
@media (max-width: 600px) {
  .announcement-banner {
    padding: 16px 40px;
  }

  .banner-image {
    max-height: 180px;
  }

  .announcement-content {
    padding: 20px;
  }

  .announcement-title {
    font-size: 18px;
  }

  .announcement-subtitle {
    font-size: 13px;
  }

  .announcement-description {
    font-size: 12.5px;
    margin-bottom: 18px;
  }

  .announcement-actions {
    flex-direction: column;
    width: 100%;

    .cta-button {
      width: 100%;
    }
  }
}
</style>
